import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { ContractOrderOutputDto, ContractOrderOutputListDto } from '@api/client';
import { useAppSelector } from 'core/store/hooks';
import { clOrdersSelector } from '../../_redux/selectors';
import { formatCurrency } from 'shared/utils/common.utils';

interface MappedClOrder {
  id: MappedFieldOrder;
  idExternalContract: MappedFieldOrder;
  object: MappedFieldOrder;
  account: MappedFieldOrder;
  poNumber: MappedFieldOrder;
  amount: MappedFieldOrder;
  actualRevenues: MappedFieldOrder;
  sales: MappedFieldOrder;
  status: MappedFieldOrder;
  clAm: MappedFieldOrder;
  legalEntity: MappedFieldOrder;
  signatureDate: MappedFieldOrder;
  azione: MappedFieldOrder;
}

interface MappedFieldOrder {
  label: string;
  value: string | number | undefined;
  visible: boolean;
  statusBadge?: string;
  isButton?: boolean;
  icon?: string;
  className?: string;
  tooltip?: string;
}

const useGetAndMapClOrders = () => {
  const { t } = useTranslation();
  const [orders, setOrders] = useState<MappedClOrder[]>([]);
  const [paginatorData, setPaginatorData] = useState<Partial<ContractOrderOutputListDto> | undefined>(undefined);

  const filteredOrders = useAppSelector(clOrdersSelector);

  useEffect(() => {
    const orderList = filteredOrders?.contractOrderOutputDtoList;
    if (orderList?.length) {
      setOrders(
        mapOrders(orderList)?.sort((a, b) =>
          new Date(a.signatureDate.value!).getTime() < new Date(b.signatureDate.value!).getTime() ? 1 : -1
        )
      );
      setPaginatorData({
        totalElements: filteredOrders?.totalElements,
        totalPages: filteredOrders?.totalPages,
        page: filteredOrders?.page,
      });
    } else {
      setOrders([]);
    }
  }, [filteredOrders]);

  const mapOrders = (orders: ContractOrderOutputDto[]): MappedClOrder[] => {
    return orders.map((order) => {
      return {
        id: { label: t('project-list.id'), value: order.pkOrderSF, visible: false },
        idExternalContract: { label: t('Order'), value: order.idExternalContract, visible: true },
        object: {
          label: t('Order name'),
          value: order.object || '-',
          visible: true,
        },
        account: {
          label: t('Account'),
          value: order.denominazioneCliente || '-',
          visible: true,
        },
        poNumber: {
          label: t('PO number'),
          value: order.ordPoNumber || '-',
          visible: true,
        },
        amount: {
          label: t('Contract value (€)'),
          value: (order.contractValue && formatCurrency(order.contractValue)) || '-',
          visible: true,
          className: 'text-end',
        },
        actualRevenues: {
          label: t('A. Revenues (€)'),
          value: (order.contractValue && formatCurrency(order.ordARicavi)) || '-',
          visible: true,
          className: 'text-end',
        },
        sales: {
          label: t('Sales (€)'),
          value: (order.ordFattureEmesse && formatCurrency(order.ordFattureEmesse)) || '-',
          visible: true,
          className: 'text-end',
        },
        status: {
          label: t('Status'),
          value: order.ordStato || '-',
          visible: true,
        },
        clAm: {
          label: t('CL/AM'),
          value: order.strutturaFunzione?.nome || '-',
          visible: true,
        },
        legalEntity: {
          label: t('Legal entity'),
          value: `${order.legalEntity?.nome || '-'}`,
          visible: true,
        },
        signatureDate: {
          label: t('Signature date'),
          value: order?.signatureDate ? format(new Date(order?.signatureDate), 'dd/MM/yyyy') : '-',
          visible: false,
        },
        azione: {
          label: t('project-list.action'),
          value: '',
          visible: true,
          isButton: true,
          icon: 'fa fas fa-arrow-right',
          className: 'text-center',
          tooltip: t('project-list.action-tooltip'),
        },
      };
    });
  };
  return { orders, paginatorData };
};

export default useGetAndMapClOrders;
